/**
 * Robot marks: how a robot's presence is drawn, on the 2D map and in the
 * 3D building view, and where (twin slice 1, plan W3 steps 10 and 12).
 *
 * "Solid means reporting now." `robot-presence.ts` decides what a robot
 * may honestly claim; this file turns that into a mark, and it is the
 * ONE place that does. ONE CLASSIFICATION, TWO RENDERERS: `robotMarks`
 * decides every mark on every floor; the 2D map draws the ones on its
 * floor (`robotMarksForLevel`) and the 3D live layer draws all of them
 * (`twin-live-layer.ts`), so the two views cannot disagree about which
 * robots are drawn, where, or how sure the drawing is. Each renderer's
 * hit test hits exactly the marks it drew, so a robot can never be
 * clickable where nothing is drawn, or drawn where a tap finds nothing.
 *
 * THE MARKS, by presence:
 *
 *   live              solid neutral dot with its heading tick
 *   quiet, checking,  the same dot at 0.6 opacity and NO heading: the
 *   settling          pose may be old (quiet), a replay (checking) or
 *                     not yet trustworthy after a ride (settling), and a
 *                     confident heading is the strongest "now" a dot can
 *                     claim
 *   lost              a dashed hollow ring at the ghost -- the newest
 *                     REAL pose known (`pickGhost`) -- or nothing when
 *                     there is none
 *   unknown, riding   nothing: an unplaced robot has no position to
 *                     draw, and a riding robot's own pose names the
 *                     floor it boarded on (the car is drawn instead).
 *                     A quiet rider (10-30 s) still draws nothing here;
 *                     a rider silent for 30 s is no longer riding but
 *                     `lost` (contract §12 A6), so it draws the ghost
 *                     ring -- at its last real pose, inside the car, on
 *                     the floor that pose names
 *
 * While the feed is CLAMPED (`feed-health.ts`) nothing is live, so a
 * live robot is drawn dim too; the canvas itself also desaturates.
 *
 * SIMULATED ROBOTS (`isSimulatedFleet`) are left out entirely for a site
 * audience (`canSeeSimulated`): the hotel must never mistake the mock
 * robot for the real one. Their planned paths go with them
 * (`trajectoriesForAudience`): a path with no dot under it would be the
 * mock robot announcing itself anyway. Shown (ops), a simulated robot
 * keeps the mark its presence earns -- a hollow or faded body would
 * read as "No signal" -- and says what it is in words instead: each
 * mark carries `simulated`, and the 3D view puts a "SIM" tag on it.
 *
 * One colour for every robot (`--nx-scene-robot`). The old per-fleet
 * hue said nothing a reader could use, and a colour is no place to put
 * certainty: certainty is carried by fill, opacity and line style.
 */
import type { RobotStateView } from '../../api/rmf-client';
import type { TrajectorySnapshot } from '../../api/ws-types';
import { robotReceiptKey } from '../../api/robot-receipts';
import { isSimulatedFleet } from '../../state/simulated-fleet';
import type { RobotPresence } from './robot-presence';

export type RobotMarkLook = 'solid' | 'dim' | 'ghost';

export interface RobotMarkStyle {
  readonly look: RobotMarkLook;
  /** Applied to the whole mark, label included. */
  readonly opacity: number;
  /** A ring with no fill: the robot is not here now. */
  readonly hollow: boolean;
  /** Canvas line dash for the ring; null for a solid line. */
  readonly dash: readonly number[] | null;
  /** Whether the heading tick is drawn. Only while live. */
  readonly heading: boolean;
}

/** Opacity of a mark that is drawn at the record's pose but cannot claim
 *  to be reporting now (quiet, checking, settling, or clamped). */
export const DIM_OPACITY = 0.6;

/** The ghost ring's dash, in screen pixels. */
export const GHOST_DASH: readonly number[] = Object.freeze([4, 4]);

const SOLID: RobotMarkStyle = Object.freeze({
  look: 'solid',
  opacity: 1,
  hollow: false,
  dash: null,
  heading: true,
});

const DIM: RobotMarkStyle = Object.freeze({
  look: 'dim',
  opacity: DIM_OPACITY,
  hollow: false,
  dash: null,
  heading: false,
});

const GHOST: RobotMarkStyle = Object.freeze({
  look: 'ghost',
  opacity: 1,
  hollow: true,
  dash: GHOST_DASH,
  heading: false,
});

/** The three looks by name, for a renderer's own tests. */
export const ROBOT_MARK_STYLES: Readonly<Record<RobotMarkLook, RobotMarkStyle>> = Object.freeze({
  solid: SOLID,
  dim: DIM,
  ghost: GHOST,
});

/**
 * The mark for one robot's presence, or null for no mark at all. No
 * presence (a robot the tracker has not classified) is no mark: a robot
 * nobody can vouch for is never drawn as if it were reporting.
 */
export function robotMarkStyle(
  presence: RobotPresence | undefined,
  clamped: boolean,
): RobotMarkStyle | null {
  if (!presence || !presence.pose) return null;
  switch (presence.kind) {
    case 'live':
      return clamped ? DIM : SOLID;
    case 'quiet':
    case 'checking':
    case 'settling':
      return DIM;
    case 'lost':
      return GHOST;
    case 'riding':
    case 'unknown':
      return null;
  }
}

/** One mark to draw (and to hit). */
export interface RobotMark {
  /** `<fleet>/<robot>`. */
  readonly key: string;
  /** Bare robot name: what a tap hands to `onSelectRobot`. */
  readonly name: string;
  readonly fleet: string;
  /** The level the mark stands on: its pose's, as the record spells it
   *  (the 3D view joins it to the geometry's levels zero-padded). */
  readonly level: string;
  /** A simulated fleet's robot (`isSimulatedFleet`). Only ever true where
   *  the audience may see simulated robots at all. */
  readonly simulated: boolean;
  /** World position: the record's pose, or the ghost's. */
  readonly x: number;
  readonly y: number;
  /** Radians; null when the pose carries none. */
  readonly yaw: number | null;
  readonly style: RobotMarkStyle;
}

/** What the marks are drawn from, besides the robots themselves. */
export interface RobotMarkView {
  /** `useRobotPresence`'s output; undefined draws nothing. */
  readonly presence: ReadonlyMap<string, RobotPresence> | undefined;
  /** The feed is clamped: nothing may look live. */
  readonly clamped: boolean;
  /** `canSeeSimulated(roles)`: false hides simulated fleets entirely. */
  readonly showSimulated: boolean;
}

/**
 * Every mark, on every floor: THE decision both views draw from. A mark
 * belongs to the level its POSE is on -- for a ghost that is where the
 * robot was last really seen, not whatever the current record (a stub,
 * the origin sentinel) claims. In the robots' own order.
 */
export function robotMarks(
  robots: readonly Pick<RobotStateView, 'name' | 'fleet'>[] | undefined,
  view: RobotMarkView,
): RobotMark[] {
  const out: RobotMark[] = [];
  if (!robots?.length || !view.presence) return out;
  for (const robot of robots) {
    const simulated = isSimulatedFleet(robot.fleet);
    if (!view.showSimulated && simulated) continue;
    const key = robotReceiptKey(robot.fleet, robot.name);
    const presence = view.presence.get(key);
    const style = robotMarkStyle(presence, view.clamped);
    const pose = presence?.pose;
    if (!style || !pose || !pose.map) continue;
    if (!Number.isFinite(pose.x) || !Number.isFinite(pose.y)) continue;
    out.push({
      key,
      name: robot.name,
      fleet: robot.fleet,
      level: pose.map,
      simulated,
      x: pose.x,
      y: pose.y,
      yaw: typeof pose.yaw === 'number' && Number.isFinite(pose.yaw) ? pose.yaw : null,
      style,
    });
  }
  return out;
}

/**
 * Every mark on `level`: THE predicate the 2D renderer and its hit test
 * share, cut from `robotMarks`.
 */
export function robotMarksForLevel(
  robots: readonly Pick<RobotStateView, 'name' | 'fleet'>[] | undefined,
  view: RobotMarkView,
  level: string,
): RobotMark[] {
  if (!level) return [];
  return robotMarks(robots, view).filter((m) => m.level === level);
}

/**
 * One string for everything a list of marks draws: who, on which floor,
 * where, facing which way, in which look, and whether it is tagged
 * simulated. Equal strings draw the same picture, so a view can rebuild
 * only when it changes -- a parked fleet re-stamped every second, or a
 * clamp that dims nothing, costs nothing.
 */
export function robotMarksSignature(marks: readonly RobotMark[]): string {
  return marks
    .map((m) =>
      [m.key, m.level, m.x, m.y, m.yaw ?? '', m.style.look, m.style.heading, m.simulated].join('|'),
    )
    .join(' ');
}

/**
 * The trajectory snapshot this audience may see: without
 * `showSimulated`, every simulated fleet's path is left out, by the same
 * fleet predicate the marks use. The snapshot comes back as it was (the
 * same object) when nothing is left out, so a memo keyed on it holds.
 */
export function trajectoriesForAudience(
  snapshot: TrajectorySnapshot | undefined,
  showSimulated: boolean,
): TrajectorySnapshot | undefined {
  if (!snapshot || showSimulated) return snapshot;
  const all = snapshot.trajectories ?? [];
  const kept = all.filter((t) => !isSimulatedFleet(t.fleet_name));
  return kept.length === all.length ? snapshot : { ...snapshot, trajectories: kept };
}
